/**
 * "동네 톡톡 관리" — moderation queue for community_talk posts.
 * Backend: GET/PATCH /api/admin/community-talk (functions/api/admin/community-talk/*),
 * admin-only via requireAdminKey. The public GET /api/community-talk always
 * filters to status="published", so this is the only place admins can see
 * hidden/deleted posts and change status.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EyeOff, MessagesSquare, RotateCcw, Trash2 } from "lucide-react";
import { apiGet, apiPatch } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AdminPageLayout from "./AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type TalkStatus = "published" | "hidden" | "deleted";

type TalkPost = {
  id: number;
  title: string;
  body: string | null;
  category: string | null;
  status: TalkStatus;
  views: number;
  likes: number;
  commentCount: number;
  authorId: number;
  authorNickname: string | null;
  authorEmail: string | null;
  createdAt: string;
  updatedAt: string | null;
};

type TalkResponse = { ok: true; items: TalkPost[]; total: number; limit: number; offset: number };

const statusOptions: { value: TalkStatus | "all"; label: string }[] = [
  { value: "all", label: "전체" },
  { value: "published", label: "게시중" },
  { value: "hidden", label: "숨김" },
  { value: "deleted", label: "삭제됨" },
];
const statusLabel: Record<string, string> = Object.fromEntries(statusOptions.map((o) => [o.value, o.label]));

export default function CommunityTalkModeration() {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("all");
  const [q, setQ] = useState("");

  const params = new URLSearchParams();
  if (status !== "all") params.set("status", status);
  if (q.trim()) params.set("q", q.trim());
  const queryString = params.toString();

  const list = useQuery({
    queryKey: ["admin-community-talk", status, q],
    queryFn: () => apiGet<TalkResponse>(`/api/admin/community-talk${queryString ? `?${queryString}` : ""}`),
  });

  const patch = useMutation({
    mutationFn: ({ id, status: newStatus }: { id: number; status: TalkStatus }) =>
      apiPatch(`/api/admin/community-talk/${id}`, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-community-talk"] });
      toast({ title: "게시글 상태를 변경했습니다" });
    },
    onError: (error: Error) => toast({ title: "변경 실패", description: error.message, variant: "destructive" }),
  });

  const items = list.data?.items || [];

  return (
    <AdminPageLayout
      title="동네 톡톡 관리"
      description="동네 톡톡 게시글을 확인하고 숨김·삭제·복구 처리를 합니다."
      icon={<MessagesSquare className="w-8 h-8" />}
      loading={list.isLoading}
      error={list.error ? (list.error as Error).message : null}
    >
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="제목·내용 검색"
              className="w-64"
            />
            <div className="ml-auto text-sm text-muted-foreground self-center">
              {list.data ? `총 ${list.data.total}건` : ""}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>제목</TableHead>
                  <TableHead>작성자</TableHead>
                  <TableHead>카테고리</TableHead>
                  <TableHead>조회/좋아요/댓글</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell className="max-w-xs">
                      <div className="truncate font-medium" title={post.title}>{post.title}</div>
                      <div className="truncate text-xs text-muted-foreground" title={post.body || ""}>
                        {post.body}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(post.createdAt).toLocaleString("ko-KR")}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {post.authorNickname || post.authorEmail || `#${post.authorId}`}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{post.category || "general"}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {post.views} / {post.likes} / {post.commentCount}
                    </TableCell>
                    <TableCell>
                      <Badge variant={post.status === "published" ? "default" : post.status === "hidden" ? "secondary" : "outline"}>
                        {statusLabel[post.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {post.status !== "published" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => patch.mutate({ id: post.id, status: "published" })}
                          >
                            <RotateCcw className="mr-1 h-3 w-3" />게시 복구
                          </Button>
                        )}
                        {post.status !== "hidden" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => patch.mutate({ id: post.id, status: "hidden" })}
                          >
                            <EyeOff className="mr-1 h-3 w-3" />숨김
                          </Button>
                        )}
                        {post.status !== "deleted" && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => patch.mutate({ id: post.id, status: "deleted" })}
                          >
                            <Trash2 className="mr-1 h-3 w-3" />삭제
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      해당 조건의 게시글이 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
